import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api.js";

export interface ConversationRow {
  id: number;
  mode: "bot" | "human";
  unread_count: number;
  last_activity_at: string | null;
  last_message_preview: string | null;
  is_ctwa: boolean;
  needs_human: boolean;
  contact_id: number;
  /** Operator-set custom name; display falls back to wa_name (the customer's own
   *  WhatsApp profile name) then phone. */
  name: string | null;
  wa_name: string | null;
  phone: string;
  optin: boolean;
  window_open: boolean | null;
  cap_hours: number | null;
  // ---- Rich list/rail fields (Phase-4; the server does not return these yet,
  // so they are optional and every consumer degrades gracefully when absent). ----
  /** Applied labels — feeds the .conv-labels chips + the dynamic filter chips. */
  labels?: string[] | null;
  /** Computed customer segment (segmentFor) — thread sub-head + rail pill. */
  segment?: string | null;
  /** Resolved referral ad/campaign names — violet .conv-ad tag + thread ad card. */
  referral_ad_name?: string | null;
  referral_campaign?: string | null;
  /** CTWA ad creative context (0017): headline/body from the referral, the ad's
   *  permalink, and a locally-cached ad image (media id, served same-origin). */
  referral_headline?: string | null;
  referral_body?: string | null;
  referral_source_url?: string | null;
  referral_media_type?: string | null;
  referral_media_id?: number | null;
  /** Context-rail fields (orders/spend/source/region). */
  orders_count?: number | null;
  total_spend?: number | null;
  source_store?: string | null;
  region?: string | null;
}

/** Per-message CTWA ad context stamped by the server on referral-carrying
 *  inbound messages (payload.referral) — a repeat customer can arrive from a
 *  DIFFERENT ad than the thread's first-touch attribution. */
export interface MessageAdReferral {
  sourceId?: string | null;
  sourceType?: string | null;
  sourceUrl?: string | null;
  headline?: string | null;
  body?: string | null;
  mediaType?: string | null;
  adId?: string;
  adName?: string;
  campaignName?: string;
  /** Locally-cached ad image (media row id, served same-origin). */
  mediaId?: number;
}

export interface ThreadMessage {
  id: number;
  direction: "in" | "out";
  senderKind: string;
  type: string;
  body: string | null;
  status: string;
  createdAt: string;
  /** Agent display name for outgoing .bubble-tag headers (Phase-4; optional). */
  agentName?: string | null;
  /** Template send provenance (0018): chip above broadcast/template bubbles. */
  templateId?: number | null;
  templateName?: string | null;
  // ---- Media + structured payload (full media support) ----
  mediaId?: number | null;
  mediaKind?: string | null; // image | video | audio | document | sticker
  mediaMime?: string | null;
  mediaFilename?: string | null;
  mediaSize?: number | null;
  mediaVoice?: boolean | null;
  mediaAnimated?: boolean | null;
  mediaDuration?: number | null;
  /** location / contacts / interactive structured data. */
  payload?: unknown;
  /** Latest customer reaction emoji shown on this message. */
  reactionEmoji?: string | null;
  // ---- Client-only optimistic fields (never sent by the server) ----
  /** Local object-URL used to paint an outbound image/video before it uploads. */
  localUrl?: string | null;
  /** 0–100 while a media upload is in flight; null once done (drives the ring). */
  uploadProgress?: number | null;
}

export interface UploadedMedia {
  id: number;
  kind: string;
  mime: string;
  filename: string | null;
  size: number;
}
export interface WindowState {
  open: boolean;
  capHours: number;
  closesAt: string | null;
}

export interface ConversationPage {
  conversations: ConversationRow[];
  total: number;
  limit: number;
  offset: number;
  /** Business-wide counts; the server sends them on the first page only. */
  meta: {
    active: number;
    need_human: number;
    unread: number;
    labels_in_use: Array<{ id: number; name: string; count: number }>;
  } | null;
}

export function useConversations(businessId: string, filters: { q: string; filter: string }) {
  return useInfiniteQuery({
    queryKey: [businessId, "conversations", filters],
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({
        business: businessId,
        q: filters.q,
        filter: filters.filter,
        limit: "25",
        offset: String(pageParam),
      });
      return apiFetch<ConversationPage>(`/api/v1/conversations?${params}`, { signal });
    },
    getNextPageParam: (last) =>
      last.offset + last.limit < last.total ? last.offset + last.limit : undefined,
  });
}

export function useConversation(businessId: string, conversationId: number | null) {
  return useQuery({
    queryKey: [businessId, "conversation", conversationId],
    queryFn: async ({ signal }) => {
      const result = await apiFetch<{ conversation: ConversationRow }>(
        `/api/v1/conversations/${conversationId}?business=${encodeURIComponent(businessId)}`,
        { signal },
      );
      return result.conversation;
    },
    enabled: conversationId != null,
    retry: false,
  });
}

export function useThread(conversationId: number | null) {
  return useQuery<{ window: WindowState; messages: ThreadMessage[] }>({
    queryKey: ["thread", conversationId],
    queryFn: () => apiFetch(`/api/v1/conversations/${conversationId}/messages`),
    enabled: conversationId != null,
  });
}

export function useSendMessage(businessId: string, conversationId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body:
        | { text: string }
        | { templateId: number; values: Record<string, string> }
        | { mediaId: number; caption?: string },
    ) =>
      apiFetch(`/api/v1/conversations/${conversationId}/messages`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["thread", conversationId] });
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
    },
  });
}

/** Upload a file as a raw octet-stream body, reporting progress via XHR. */
export function useUploadMedia(businessId: string) {
  return useMutation<UploadedMedia, Error, { file: File; onProgress?: (pct: number) => void }>({
    mutationFn: ({ file, onProgress }) =>
      new Promise<UploadedMedia>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const qs = new URLSearchParams({
          business: businessId,
          mime: file.type || "application/octet-stream",
          filename: file.name,
        }).toString();
        xhr.open("POST", `/api/v1/media?${qs}`);
        xhr.withCredentials = true;
        xhr.setRequestHeader("Content-Type", "application/octet-stream");
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText) as UploadedMedia);
          } else {
            let msg = "Upload failed";
            try {
              msg = (JSON.parse(xhr.responseText) as { error?: { message?: string } }).error
                ?.message as string;
            } catch {
              /* keep default */
            }
            reject(new Error(msg || "Upload failed"));
          }
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(file);
      }),
  });
}

/** Staff reaction on a message (emoji "" removes it). */
export function useReact(conversationId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { messageId: number; emoji: string }) =>
      apiFetch(`/api/v1/conversations/${conversationId}/react`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["thread", conversationId] });
    },
  });
}

export function useTakeover(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: number) =>
      apiFetch(`/api/v1/conversations/${conversationId}/takeover`, { method: "POST" }),
    onSuccess: (_d, conversationId) => {
      qc.invalidateQueries({ queryKey: ["thread", conversationId] });
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
    },
  });
}

export function markConversationRead(conversationId: number) {
  return apiFetch(`/api/v1/conversations/${conversationId}/read`, { method: "POST" });
}

// ---- Labels ---- (LabelRow re-exported for the label picker UI)
export interface LabelRow {
  id: number;
  name: string;
}

export function useLabels(businessId: string) {
  return useQuery<LabelRow[]>({
    queryKey: ["labels", businessId],
    queryFn: async () =>
      (await apiFetch<{ labels: LabelRow[] }>(`/api/v1/labels?business=${businessId}`)).labels,
  });
}

/** Apply a label by name (creates it on the fly if new). */
export function useApplyLabel(businessId: string, conversationId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiFetch(`/api/v1/conversations/${conversationId}/labels`, {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["labels", businessId] });
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
    },
  });
}

export function useRemoveLabel(businessId: string, conversationId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (labelId: number) =>
      apiFetch(`/api/v1/conversations/${conversationId}/labels/${labelId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
    },
  });
}

/** Rename a label everywhere it's applied (409 when the name is taken). */
export function useRenameLabel(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      apiFetch(`/api/v1/labels/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["labels", businessId] });
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
    },
  });
}

/** Delete a label for the whole business — it disappears from every chat. */
export function useDeleteLabel(businessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/api/v1/labels/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["labels", businessId] });
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
    },
  });
}

// ---- Manual orders ----
export interface OrderRow {
  id: number;
  order_ref: string;
  order_number: string | null;
  amount: string | null;
  currency: string;
  created_at: string;
  /** 'manual' rows are staff-logged (editable); 'webhook'/'backfill' are store-synced (read-only). */
  source: string;
  tracking_number: string | null;
}
export interface LogOrderBody {
  totalPrice: number;
  orderId?: string;
  currency?: string;
  sendConfirmation?: boolean;
  tracking?: { number: string; courier?: string; url?: string };
  sendTracking?: boolean;
}

export function useLogOrder(businessId: string, conversationId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LogOrderBody) =>
      apiFetch(`/api/v1/conversations/${conversationId}/order`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["thread", conversationId] });
      qc.invalidateQueries({ queryKey: ["orders", conversationId] });
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
    },
  });
}

export function useOrders(conversationId: number | null) {
  return useQuery<OrderRow[]>({
    queryKey: ["orders", conversationId],
    queryFn: async () =>
      (await apiFetch<{ orders: OrderRow[] }>(`/api/v1/conversations/${conversationId}/orders`))
        .orders,
    enabled: conversationId != null,
  });
}

export interface AddTrackingBody {
  orderRef: string;
  orderNumber?: string;
  number: string;
  courier?: string;
  url?: string;
  send?: boolean;
}

/** Edit a manually-logged order's amount / display number. */
export function useUpdateOrder(businessId: string, conversationId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; totalPrice?: number; orderNumber?: string }) =>
      apiFetch(`/api/v1/orders/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", conversationId] });
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
      qc.invalidateQueries({ queryKey: [businessId, "contacts"] }); // spend/segment shift
    },
  });
}

/** Remove a manually-logged order (spend/segment recompute server-side). */
export function useDeleteOrder(businessId: string, conversationId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/api/v1/orders/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", conversationId] });
      qc.invalidateQueries({ queryKey: [businessId, "conversations"] });
      qc.invalidateQueries({ queryKey: [businessId, "conversation"] });
      qc.invalidateQueries({ queryKey: [businessId, "contacts"] });
    },
  });
}

/** Add tracking to an existing order (manual, Woo, or Shopify) + optionally send it. */
export function useAddTracking(conversationId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddTrackingBody) =>
      apiFetch(`/api/v1/conversations/${conversationId}/order/tracking`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", conversationId] });
      qc.invalidateQueries({ queryKey: ["thread", conversationId] });
    },
  });
}
